import { stripFootnotes, tokenizeVerseText } from './verse_format_utils';

function hashToken(value) {
  let hash = 2166136261;
  Array.from(value).forEach(character => {
    hash ^= character.codePointAt(0);
    hash = Math.imul(hash, 16777619);
  });
  return hash >>> 0;
}

function firstVisibleCharacter(word) {
  return Array.from(word).find(character => /[\p{L}\p{N}]/u.test(character)) || '';
}

export function getFirstVerseWord(text) {
  const cleaned = stripFootnotes(text || '').replace(/[[\]]/g, '').trim();
  if (!cleaned) {
    return '';
  }
  if (typeof Intl.Segmenter === 'function') {
    const segmenter = new Intl.Segmenter(undefined, { granularity: 'word' });
    const word = Array.from(segmenter.segment(cleaned))
      .find(segment => segment.isWordLike);
    if (word) {
      return word.segment;
    }
  }
  return cleaned.match(/[\p{L}\p{N}]+(?:['’-][\p{L}\p{N}]+)*/u)?.[0] || '';
}

export function getBlankTokenIds(text, hiddenFraction) {
  if (!Number.isFinite(hiddenFraction) || hiddenFraction < 0 || hiddenFraction > 1) {
    throw new RangeError('Hidden fraction must be between zero and one');
  }
  const tokens = tokenizeVerseText(text);
  if (tokens.length === 0 || hiddenFraction === 0) {
    return new Set();
  }
  const hiddenCount = Math.min(
    tokens.length,
    Math.max(1, Math.round(tokens.length * hiddenFraction))
  );
  const rankedTokens = [...tokens].sort((first, second) => (
    hashToken(`${first.id}:${first.text.toLocaleLowerCase()}`)
    - hashToken(`${second.id}:${second.text.toLocaleLowerCase()}`)
    || first.id - second.id
  ));
  return new Set(rankedTokens.slice(0, hiddenCount).map(token => token.id));
}

export function getVerseInitials(text) {
  return stripFootnotes(text || '')
    .replace(/[[\]]/g, '')
    .split(/\s+/)
    .filter(Boolean)
    .map(firstVisibleCharacter)
    .filter(Boolean)
    .join('');
}

export function normalizeInitialsInput(value) {
  return Array.from(value || '')
    .filter(character => /[\p{L}\p{N}]/u.test(character))
    .join('');
}

export function acceptInitialsInput(value, correctSequence) {
  const normalized = normalizeInitialsInput(value);
  const normalizedCorrect = normalizeInitialsInput(correctSequence);
  return normalizedCorrect.toLocaleLowerCase().startsWith(normalized.toLocaleLowerCase())
    ? normalized
    : null;
}

export function getFetchedVerse(response, fallbackReference) {
  const verses = Array.isArray(response) ? response : response?.verses;
  if (!Array.isArray(verses) || verses.length === 0) {
    throw new TypeError('Bible text response did not include a verse');
  }
  const verse = verses[0];
  const text = typeof verse === 'string' ? verse : verse?.text;
  if (typeof text !== 'string' || !text.trim()) {
    throw new TypeError('Bible text response did not include verse text');
  }
  return {
    reference: (
      typeof verse?.ref === 'string' && verse.ref.trim()
        ? verse.ref.trim()
        : fallbackReference
    ),
    text: stripFootnotes(text).replace(/\s+/g, ' ').trim(),
  };
}

export function formatMemoryCountdown(milliseconds) {
  if (!Number.isFinite(milliseconds)) {
    throw new TypeError('Countdown must be milliseconds');
  }
  const totalSeconds = Math.max(0, Math.ceil(milliseconds / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return minutes > 0
    ? `${minutes}:${String(seconds).padStart(2, '0')}`
    : `${seconds}s`;
}
